import { useEffect, useRef } from 'react';
import { YouTubeAdapter } from '../audio/player/youtube.js';
import { session } from '../session.js';
import { useStudioStore } from '../stores/useStudioStore.js';
import { Icon } from './Icon.js';

interface YouTubePlayerProps {
  videoId: string;
  compact?: boolean;
}

export function YouTubePlayer({ videoId, compact = false }: YouTubePlayerProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const playerReady = useStudioStore((s) => s.playerReady);
  const setPlayer = useStudioStore((s) => s.setPlayer);
  const setSourceError = useStudioStore((s) => s.setSourceError);
  const updateProject = useStudioStore((s) => s.updateProject);
  const adapterRef = useRef<YouTubeAdapter | null>(null);

  useEffect(() => {
    if (!videoId || !hostRef.current) return;
    let cancelled = false;
    const adapter = new YouTubeAdapter();
    adapterRef.current = adapter;
    const host = hostRef.current;
    host.dataset.videoId = videoId;
    adapter.on({
      onReady: ({ duration }) => {
        if (cancelled) return;
        const meta = adapter.getVideoData();
        setPlayer({ playerReady: true, playerDuration: duration, playerState: 'cued' });
        if (meta.title) updateProject((p) => ({ ...p, youtubeTitle: meta.title, title: p.title === 'Novo projeto' ? meta.title! : p.title }));
      },
      onStateChange: (playerState) => {
        if (cancelled) return;
        setPlayer({ playerState });
      },
      onPlaybackStarted: ({ requestedAtPerf, startedAtPerf }) => {
        if (cancelled) return;
        const delayMs = Math.max(0, startedAtPerf - requestedAtPerf);
        setPlayer({ playerStartDelayMs: delayMs });
        updateProject((p) => ({ ...p, sync: { ...p.sync, playerStartDelayMs: delayMs } }));
      },
      onError: (_code, message) => {
        if (cancelled) return;
        setSourceError(message);
        setPlayer({ playerReady: false, playerState: 'unavailable' });
      },
    });
    setPlayer({ playerReady: false, playerState: 'buffering', playerDuration: 0, playerCurrentTime: 0 });
    setSourceError(null);
    session.adapter = adapter;
    session.playback.setAdapter(adapter);

    adapter.load(host).then((meta) => {
      if (cancelled) return;
      setPlayer({ playerReady: true, playerDuration: meta.duration, playerState: 'cued' });
    }).catch((err) => {
      if (cancelled) return;
      const message = err instanceof Error ? err.message : 'Não foi possível carregar o player oficial do YouTube.';
      setSourceError(message);
      setPlayer({ playerReady: false, playerState: 'unavailable' });
    });

    return () => {
      cancelled = true;
      adapter.destroy();
      if (session.adapter === adapter) {
        session.adapter = null;
        session.playback.setAdapter(null);
      }
      adapterRef.current = null;
    };
  }, [videoId, setPlayer, setSourceError, updateProject]);

  return (
    <div className={`youtube-player-shell ${compact ? 'youtube-player-compact' : ''}`}>
      <div className="youtube-frame" ref={hostRef} />
      {!playerReady && (
        <div className="player-loading">
          <div className="player-loading-icon"><Icon name="youtube" size={22} /></div>
          <div className="loading-lines"><span /><span /></div>
          <p>Conectando ao player oficial…</p>
        </div>
      )}
    </div>
  );
}
